<template>
  <div
    class="sam-multi-chip-input"
    :class="{ 'is-disabled': disabled }"
    tabindex="-1"
    v-on="disabled ? {} : $listeners"
  >
    <div
      ref="inputWrapper"
      class="sam-multi-chip-input__input-wrapper"
    >
      <!-- CHIPS -->
      <template v-if="value.length">
        <div
          ref="chipsContainer"
          class="chips__container"
        >
          <div v-if="isInputOverflown">
            <SamChip
              :id="overflownChipId"
              :label="overflownLabel"
              :disabled="disabled"
              active
            >
              <template v-slot:append>
                <SamIcon
                  icon="interface-close"
                  :size="SAMORA_SIZES.X_SMALL"
                  @click.stop="clearInput"
                />
              </template>
            </SamChip>

            <SamTooltip
              :anchor-name="overflownChipId"
              position="top"
              :message="listedValues"
              :text-overflow-ellipsis="false"
            />
          </div>
          <template v-else>
            <SamChip
              v-for="chip in value"
              :key="chip.value"
              :label="chip.label"
              :disabled="disabled"
              class="chip"
              active
            >
              <template v-slot:append>
                <SamIcon
                  v-if="!disabled"
                  icon="interface-close"
                  :size="SAMORA_SIZES.X_SMALL"
                  @click.stop="removeChip(chip)"
                />
              </template>
            </SamChip>
          </template>
        </div>
      </template>

      <!-- PLACEHOLDER -->
      <template v-else>
        <span class="sam-multi-chip-input__placeholder">
          {{ placeholder }}
        </span>
      </template>

      <div
        ref="actionsContainer"
        class="actions__container"
      >
        <SamIcon
          v-if="!disabled && value.length"
          icon="interface-close"
          class="input__clear-icon"
          :size="SAMORA_SIZES.SMALL"
          @click.stop="clearInput"
        ></SamIcon>
        <!-- @slot Use this slot to add more actions to the input -->
        <slot name="actions"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import SamChip from '../SamChip/SamChip.vue';
import SamIcon from '../SamIcon/SamIcon.vue';
import SamTooltip from '../SamTooltip/SamTooltip.vue';

import SAMORA_SIZES from '../../../utils/constants/samora-sizes.enum';
import { debounce, uuidv4 } from '../../../utils/utils';

export default {
  name: 'SamMultiChipInput',
  components: { SamIcon, SamChip, SamTooltip },
  props: {
    /**
     * The bound model value
     * @model
     */
    value: {
      type: Array,
      required: true,
      default: () => [],
      // TODO: VALIDATOR
    },
    /**
     * The input placeholder to show when no value is present
     */
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * If it is on disabled state
     * @values true, false
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    SAMORA_SIZES,
    inputSize: 'md',
    isInputOverflown: false,
  }),
  computed: {
    listedValues() {
      return this.value.map((chip) => chip.label).join(', ');
    },
    overflownChipId() {
      return `chip__${uuidv4()}`;
    },
    overflownLabel() {
      if (this.inputSize === 'sm') {
        return `${this.value.length} selected`;
      }
      if (this.inputSize === 'xs') {
        return this.value.length;
      }
      return `${this.value.length} option${this.value.length > 1 ? 's' : ''} selected`;
    },
  },
  watch: {
    value: {
      handler() {
        setTimeout(this.checkChipContainerOverflow);
      },
    },
  },
  mounted() {
    // handle window / input resizing
    if (ResizeObserver) {
      const inputWidthObserver = new ResizeObserver(debounce(this.checkChipContainerOverflow, 150));
      inputWidthObserver.observe(this.$refs.inputWrapper);
      this.checkChipContainerOverflow();
    }
  },
  methods: {
    removeChip(chip) {
      /**
       * Triggers when the chip's remove icon is pressed
       * @property {Object} chip The chip to be removed
       */
      this.$emit('remove-chip', chip);
      /**
       * Updates the bound model
       * @property {Array} value The new bound array
       */
      this.$emit(
        'input',
        this.value.filter((el) => el !== chip)
      );
    },
    clearInput() {
      /**
       * Triggers when the input's clear icon is pressed
       */
      this.$emit('clear');
      /**
       * Updates the bound model
       * @property {Array} value The new bound empty array
       */
      this.$emit('input', []);
    },
    checkChipContainerOverflow() {
      this.isInputOverflown = false;

      if (
        !this.value.length ||
        [this.$refs.chipsContainer, this.$refs.actionsContainer, this.$refs.inputWrapper].some(
          (ref) => !ref
        )
      )
        return;

      this.$nextTick(() => {
        const { clientWidth: chipsContainerWidth } = this.$refs.chipsContainer;
        const { clientWidth: actionsContainerWidth } = this.$refs.actionsContainer;
        const { clientWidth: inputWrapperWidth } = this.$refs.inputWrapper;

        this.isInputOverflown = chipsContainerWidth >= inputWrapperWidth - actionsContainerWidth;

        if (this.isInputOverflown) {
          if (chipsContainerWidth < 150) {
            this.inputSize = 'xs';
          } else if (chipsContainerWidth < 200) {
            this.inputSize = 'sm';
          } else {
            this.inputSize = 'md';
          }
        }
      });
    },
  },
};
</script>

<style scoped lang="scss">
.sam-multi-chip-input {
  @extend .d-block, .px-4, .py-2;
  box-sizing: border-box;
  width: 100%;
  overflow: hidden;
  height: 51px;
  border: $size--0--fourth solid $neutral--60;
  border-radius: $size--1;
  cursor: pointer;

  &:hover:not(.is-disabled) {
    border-color: $informative--hover;
  }

  &:focus:not(.is-disabled) {
    outline: 3px solid $informative--border;
  }

  &__placeholder {
    @extend .text--body-1, .py-1;
    color: $neutral--80;
  }

  &__input-wrapper {
    @extend .d-flex;
    min-height: 100%;

    .chips__container {
      @extend .d-inline-flex;
      white-space: nowrap;
      max-width: 100%;

      .chip {
        &:not(:last-child) {
          @extend .mr-2;
        }
      }
    }

    .actions__container {
      @extend .d-flex, .ml-auto, .items-center;
      color: $neutral--90;

      .input__clear-icon {
        @extend .ml-2;
        cursor: pointer;
      }

      > *:not(:last-child) {
        @extend .pr-2;
      }
    }
  }

  &.is-disabled {
    cursor: not-allowed;
    border-color: $neutral--40;
    background-color: $neutral--20;

    .actions__container {
      color: $neutral--50;
    }
  }
}
</style>
